/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, HostListener, OnInit} from '@angular/core';
import {ColDef} from 'ag-grid-community';
import {ActionsCellRendererComponent} from '../cell-renderer/actions-cell-renderer.component';
import {UserSearchCellEditorComponent} from '../cell-renderer/user-search-cell-editor.component';
import {EditableGridComponent} from '../editable-grid-component';
import {GpsRpo} from '../../model/gps-rpo';

@Component({
    selector: 'app-gps-rpo-management',
    templateUrl: './gps-rpo-management.component.html',
    styleUrls: ['./gps-rpo-management.component.scss'],
    standalone: false
})
export class GpsRpoManagementComponent extends EditableGridComponent implements OnInit {
    permissionUrl = '/esciUI/gpsRpoViewPermission';
    adminPermissionUrl = '/esciUI/gpsRpoAdminPermission';
    getGpsRposUrl = '/esciUI/getGpsRpos/fromRpoManagement';
    saveUrl = '/esciUI/saveGpsRpos';
    rowData: GpsRpo[] = [];
    missingGpsRpos: GpsRpo[] = [];
    adminOnlyColumns = ['actions'];
    cols: ColDef[] = [
        {minWidth: 130, maxWidth: 130, headerName: 'Actions', colId: 'actions', editable: false,
            filter: false, sortable: false, lockPosition: true, suppressMovable: true,
            cellRenderer: ActionsCellRendererComponent,
            cellRendererParams: {
                onDeleteClick: (function (params: any): void {
                    this.onDeleteClick(params, this.rowData, (updatedData) => this.refreshGrid(updatedData));
                }).bind(this),
                onCopyClick: this.onCopyClick.bind(this),
                fieldForDeleteMessage: 'rpo'
            }
        },
        {field: 'id', headerName: 'GPS RPO ID', hide: true},
        {field: 'rpo', headerName: 'GPS RPO', maxWidth: 160},
        {
            field: 'architect', headerName: 'Electrical Architect',
            valueGetter: (params) => this.getValueFromUser(params.data['architect']),
            filterValueGetter: (params) => this.getValueFromUser(params.data['architect']),
            cellRenderer: UserSearchCellEditorComponent,
            cellRendererParams: {
                userField: 'architect',
                readonly: true
            },
            minWidth: 280,
            cellEditor: UserSearchCellEditorComponent,
            cellEditorParams: {
                onSelectedUser: this.onSelectedUser.bind(this),
            }
        },
        {
            field: 'dse', headerName: 'Design System Engineer',
            valueGetter: (params) => this.getValueFromUser(params.data['dse']),
            filterValueGetter: (params) => this.getValueFromUser(params.data['dse']),
            cellRenderer: UserSearchCellEditorComponent,
            cellRendererParams: {
                userField: 'dse',
                readonly: true
            },
            minWidth: 280,
            cellEditor: UserSearchCellEditorComponent,
            cellEditorParams: {
                onSelectedUser: this.onSelectedUser.bind(this),
            }
        },
        {field: 'type', headerName: 'GPS Type'},
        {field: 'programFamily', headerName: 'GPS Program Family'},
        {field: 'controller', headerName: 'Controller'}
        ];

    ngOnInit(): void {
        this.cols.forEach(col => {
            col.tooltipValueGetter = (params) => {
                return params.data.changes.get(col.field);
            };
        });
        this.permission.set(false);
        this.esciService.getPermissionAndFileSizeLimit(this, false);
        this.esciService.getGpsRpos(this, () => {
            this.setColumnsVisible();
            this.reset();
            if (this.missingGpsRpos?.length) {
                const sortedRpos = Array.from(this.missingGpsRpos).map(o => o.rpo).sort((a, b) => a.localeCompare(b));
                this.confirmationService.confirm({
                    message: `The following RPOs were found in Momentum Programs but not in your list of GPS RPOs. <br/>
                      You must add them here in order for timings to appear. <br/><br/>
                      <b>Missing RPOs:</b> ${sortedRpos.join(', ')}`,
                    header: 'Missing RPOs',
                    key: 'missingGpsRposConfirmation',
                    icon: 'pi pi-exclamation-triangle',
                    acceptButtonStyleClass: 'p-button-success',
                    rejectIcon: 'pi pi-check mr-2',
                    acceptIcon: 'pi pi-external-link mr-2',
                    rejectLabel: 'Ignore',
                    acceptLabel: 'Add Them Now',
                    accept: () => {
                        this.missingGpsRpos.forEach(gpsRpo => {
                            gpsRpo.state = 'new';
                            gpsRpo.id = -1;
                            gpsRpo.type = '';
                            gpsRpo.programFamily = '';
                            gpsRpo.controller = '';
                            this.rowData.push(gpsRpo);
                        })
                        this.messageService.processSuccess('Added the following RPOs from Momentum: ' + sortedRpos.join(', '));
                        if (this.isGridApiAlive(this.gridApi)) {
                            this.gridApi.setFilterModel({
                                id: {
                                    type: 'equals',
                                    filter: -1
                                }
                            });
                            this.gridApi.onFilterChanged();
                        }
                        this.reset();
                    }
                });
            }
        });
    }

    saveGpsRpos() {
        if (this.areRowsValid(this.rowData) && this.adminPermission()) {
            this.saving = true;
            // remove any rows where state is deleted
            this.setRowData(this.rowData.filter(row => row.state !== 'deleted'));
            this.esciService.saveRows(this, this.rowData, 'GPS RPO',
                () => this.esciService.getGpsRpos(this));
        }
    }

    exportReport() {
        // export all data in the grid
        this.excelService.exportGridDataAsExcelFile(this.gridApi, 'GPS_RPO_Report');
    }
    // Callback for user selected
    onSelectedUser(params, newUser): void {
        // find the matching row by id, rpo, type, etc. and set the new user
        // id may not match if we have multiple new rows with id -1
        const field = params.colDef.field;
        const oldRow = params.data;
        const oldRowUser = oldRow[field];
        const oldGmin = oldRowUser?.gmin;
        if (oldGmin === newUser?.gmin) {
            return;
        }
        if (oldGmin) {
            const oldName = oldRowUser.fullName ? oldRowUser.fullName : oldRowUser.firstLast();
            const newName = newUser.fullName;
            // popup confirmation message to ask if user wants to replace every row user with new user gmin
            this.confirmationService.confirm({
                message: `Do you want to replace all instances of ${oldName} in the ${field} field with the ${newName}?`,
                key: 'updateUserConfirmation',
                header: 'Replace User Confirmation',
                accept: () => {
                    let changeCount = 0;
                    for (const row of this.rowData) {
                        if (row[field]?.gmin === oldGmin) {
                            row[field] = newUser;
                            row['dataChanged'] = true;
                            this.dataChanged = true;
                            changeCount++;
                        }
                    }
                    console.log(`Updated the user in ${changeCount} rows for field ${field}`);
                    this.areRowsValid(this.rowData);
                    this.refreshGrid(this.rowData);
                },
                reject: () => {
                    for (const row of this.rowData) {
                        // if row id is -1, we need to make sure the rpo also matches
                        // otherwise, we just need row id to match
                        if ((row.id === -1 && row.rpo === oldRow.rpo) || row.id === oldRow.id) {
                            row[field] = newUser;
                            row['dataChanged'] = true;
                            this.dataChanged = true;
                            break;
                        }
                    }
                    this.areRowsValid(this.rowData);
                    this.refreshGrid(this.rowData);
                }
            });
        }
    }
    onCopyClick(params): void {
        // find the index of the row clicked on
        const index = this.rowData.findIndex(row => row.id === params.data.id);
        // copy the row and add it to the array at the index
        const newRow = structuredClone(params.data);
        newRow.id = -1;
        newRow.state = 'new';
        this.rowData.splice(index + 1, 0, newRow);
        this.dataChanged = true;
        // update the grid
        this.refreshGrid(this.rowData);
    }

    resetChanges() {
        this.resetting = true;
        this.exporting = true;
        this.messageService.clear();
        this.esciService.getGpsRpos(this, () => this.reset());
    }
    reset() {
        super.reset();
        this.refreshGrid(this.rowData);
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        if (this.gridApi && !this.gridApi.isDestroyed()) {
            this.gridApi.sizeColumnsToFit();
        }
    }
}
